// Reading the response of PUT /api/project/:slug/supplier/date/bulk.
//
// Every value here is half of a cross-tier contract with src/api/project.js: the wire error key,
// the recipient ceiling, and the shape of the `failed` entries. The tiers share no module (CRA
// confines the client to src/cms/src), so the copies are kept honest mechanically instead:
// src/tests/bulkDeadlineContract.spec.js reads both files and fails if they drift. Renaming one
// side — even together with that side's own spec — turns that lock red.

// Ceiling on contact people one bulk deadline extension may notify. Mirrors
// BULK_DATE_MAX_RECIPIENTS in src/api/project.js, which enforces it — this copy exists only to show
// and pre-empt the limit in the UI; the server stays the authority on whether a send is refused.
export const BULK_DEADLINE_MAX_RECIPIENTS = 350;

// The `error` value the route returns with its 429. Matched exactly: any other value means a
// failure this UI has no specific advice for, and the caller should fall back to generic text.
export const BULK_DEADLINE_LIMIT_ERROR = 'recipients.exceeded.the.limit';

// The `error` value on the route's 400 when the selection holds more purchases than one request
// may carry. Every other validation failure is a bare Joi 400 with no `error` key, which is why
// this one is matched rather than the status: they need different advice.
export const BULK_DEADLINE_AREAS_ERROR = 'areas.exceeded.the.limit';

/**
 * One entry of the route's `failed` array. A whole area that could not be resolved carries no
 * `supplierId`; a single supplier that could not be extended does.
 */
export interface BulkDeadlineFailure {
  areaId: string;
  supplierId?: string | null;
  error?: string;
}

/**
 * Anything the route sends that can carry failures. Both a completed run and a refusal do: the
 * route reports the areas it could not resolve even when it declines to send.
 */
export interface BulkDeadlineFailureCarrier {
  failed?: BulkDeadlineFailure[];
}

export interface BulkDeadlineResult extends BulkDeadlineFailureCarrier {
  updated: number;
}

/** The 429 body: why it was refused, by how much, and what could not be resolved along the way. */
export interface BulkDeadlineRefusal extends BulkDeadlineFailureCarrier {
  error?: string;
  limit?: number;
  requested?: number;
}

export interface BulkDeadlineSupplierFailure {
  areaId: string;
  supplierId: string;
}

export interface BulkDeadlineFailureSummary {
  failedSuppliers: number;
  failedAreas: number;
  /** Whole-purchase failures, de-duplicated, in report order. Nothing in these was sent. */
  failedAreaIds: string[];
  /** Individual suppliers that failed inside purchases that otherwise went through. */
  failedSupplierEntries: BulkDeadlineSupplierFailure[];
}

/** An axios-shaped rejection; only the fields this contract reads are described. */
export interface BulkDeadlineError {
  response?: {
    status?: number;
    data?: BulkDeadlineRefusal;
  };
}

export type BulkDeadlineErrorReason =
  // The selection reaches more contact people than one send may cover. `values` carries the
  // server's own numbers ready for interpolation, so the caller forwards them rather than choosing
  // between these and its local copy of the limit — which is the staler of the two whenever the
  // server's ceiling has moved.
  | { kind: 'tooManyRecipients'; values: { requested?: number; limit?: number } }
  // The selection holds more purchases than one request may carry. Distinct from the recipient cap:
  // this one counts purchases, and is refused before anything is resolved.
  | { kind: 'tooManyPurchases'; values: { requested?: number; limit?: number } };

/**
 * Splits a `failed` array into the two things that actually went wrong, so a bulk result can be
 * reported as what it is. Whole failed areas are counted as areas — counting one as a single
 * supplier would understate a purchase that never got processed at all.
 *
 * Areas are de-duplicated (the same area can be reported once per pass), suppliers are not: each
 * supplier entry is a distinct supplier that did not get their deadline extended.
 */
export function summarizeBulkDeadlineFailures(
  carrier?: BulkDeadlineFailureCarrier | null,
): BulkDeadlineFailureSummary {
  const failed = carrier?.failed ?? [];
  const failedSupplierEntries = failed
    .filter((entry) => entry.supplierId)
    .map((entry) => ({ areaId: entry.areaId, supplierId: entry.supplierId as string }));
  const failedAreaIds = Array.from(new Set(
    failed.filter((entry) => !entry.supplierId).map((entry) => entry.areaId),
  ));
  return {
    failedSuppliers: failedSupplierEntries.length,
    failedAreas: failedAreaIds.length,
    failedAreaIds,
    failedSupplierEntries,
  };
}

/**
 * What stays selected after a bulk send that partly succeeded: exactly the whole-purchase
 * failures, and never the purchases where only some suppliers failed.
 *
 * The split is about retry safety, not tidiness. A whole-area failure means the route's first
 * pass could not resolve the purchase, so nobody in it was emailed — retrying it sends for the
 * first time. A partly-failed purchase already emailed its successful suppliers, and the route
 * re-extends (and re-emails) everyone in a purchase it is given, so retaining it would turn the
 * obvious retry click into duplicate mail. Those suppliers are named in the failure toast
 * instead; the single-supplier flow is the targeted tool for them.
 */
export function selectionAfterBulkDeadline(
  carrier?: BulkDeadlineFailureCarrier | null,
): Set<string> | null {
  const { failedAreaIds } = summarizeBulkDeadlineFailures(carrier);
  return failedAreaIds.length > 0 ? new Set(failedAreaIds) : null;
}

/**
 * Whether a completed run extended anyone at all.
 *
 * The route answers 200 with `updated: 0` when every supplier failed — a run that reached nobody is
 * still a well-formed response, not an HTTP error. Anything reporting the outcome has to read this
 * rather than the status, or a send that notified no one announces itself as saved.
 */
export function didExtendAnything(result?: BulkDeadlineResult | null): boolean {
  return (result?.updated ?? 0) > 0;
}

/**
 * Classifies a failed bulk extension into the rejections the user can act on. Returns undefined for
 * everything else — transient failures where the generic "try again" is the right advice, and where
 * inventing specific text would be worse than saying nothing.
 */
export function classifyBulkDeadlineError(
  error?: BulkDeadlineError | null,
): BulkDeadlineErrorReason | undefined {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status === 429 && data?.error === BULK_DEADLINE_LIMIT_ERROR) {
    return { kind: 'tooManyRecipients', values: { requested: data.requested, limit: data.limit } };
  }
  if (status === 400 && data?.error === BULK_DEADLINE_AREAS_ERROR) {
    return { kind: 'tooManyPurchases', values: { requested: data.requested, limit: data.limit } };
  }
  // Any other 400 is one of the route's bare Joi rejections — an empty selection left behind by a
  // refresh, a missing date. Telling the user to shrink a selection would be wrong for all of them,
  // so they fall through to the generic message.
  return undefined;
}
